import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { Camera, Download, EnvelopeSimple, CaretDown, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../lib/api";

/** Phase 249 — Generate / email a Before & After PDF report for a project.
 * Renders as a split button: primary action = download, dropdown = email.
 */
export default function BeforeAfterReportButton({ project }) {
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: project?.client_email || project?.client_contact_email || "",
    subject: `Before & After Report — ${project?.name || "Project"}`,
    message: "",
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await API.get(`/projects/${project.id}/before-after.pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `before-after-${(project.name || "project").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Before & After PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate report");
    } finally { setDownloading(false); }
  };

  const handleSend = async () => {
    if (!emailForm.to || !emailForm.to.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      await API.post(`/projects/${project.id}/before-after-email`, emailForm);
      toast.success(`Report emailed to ${emailForm.to}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  return (
    <>
      <div className="inline-flex items-stretch shadow-sm rounded-md overflow-hidden" data-testid="before-after-button-group">
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={downloading}
          className="rounded-r-none border-r-0"
          data-testid="before-after-download-btn"
        >
          {downloading ? <Spinner size={14} className="animate-spin mr-1.5" /> : <Camera size={14} className="mr-1.5" weight="duotone" />}
          Before &amp; After
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-l-none px-2" data-testid="before-after-menu-btn">
              <CaretDown size={12} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleDownload} data-testid="before-after-menu-download">
              <Download size={14} className="mr-2" /> Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setEmailOpen(true)} data-testid="before-after-menu-email">
              <EnvelopeSimple size={14} className="mr-2" /> Email to client…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="before-after-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Email Before &amp; After Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Recipient</label>
              <Input
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                placeholder="client@example.com"
                data-testid="before-after-email-to"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Subject</label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                data-testid="before-after-email-subject"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Message (optional)</label>
              <Textarea
                rows={4}
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Add a short note that appears at the top of the email and inside the PDF…"
                className="resize-none"
                data-testid="before-after-email-message"
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              The PDF compiles every task on this project that has at least one before / after photo,
              with side-by-side comparisons, your branding, and any team notes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)} data-testid="before-after-email-cancel">Cancel</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="before-after-email-send">
              {sending ? <Spinner size={14} className="animate-spin mr-1.5" /> : <EnvelopeSimple size={14} className="mr-1.5" />}
              Send report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
